import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Search, Plus, Building2, ChevronDown, Check, ArrowLeft, Building } from 'lucide-react'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

// Mock Data
const clients = [
  { id: '1', name: 'Jet Star Aviation' },
  { id: '2', name: 'Acme Corp' },
  { id: '3', name: 'Global Flight Services' },
  { id: '4', name: 'Delta Dynamics' },
]

export function SelectOrAddNewVariationsPage() {
  return (
    <div className="min-h-screen bg-slate-50/50 p-8 pb-24">
      <div className="max-w-3xl mx-auto space-y-12">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Target Action: Select or Add New</h1>
          <p className="text-slate-600 mt-2 text-lg">
            3 mock variations to improve the UX for selecting an item from a list or adding a new one.
          </p>
        </div>
        
        <Variation1 />
        <Variation2 />
        <Variation3 />
      </div>
    </div>
  )
}

// -------------------------------------------------------------------------------- //
// VARIATION 1: Integrated Combobox
// -------------------------------------------------------------------------------- //
function Variation1() {
  const [open, setOpen] = useState(false)
  const [selectedClient, setSelectedClient] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  return (
    <div className="space-y-4">
      <div className="border-b pb-2">
        <h2 className="text-xl font-semibold text-slate-900">Variation 1: The Integrated Combobox</h2>
        <p className="text-slate-500 text-sm">
          A traditional combobox that includes a sticky "Add New" action over or below the search results. 
          Keeps the UI absolutely minimal until the user decides they need to create.
        </p>
      </div>

      <Card className="shadow-sm border-slate-200">
        <CardContent className="p-6">
          <div className="space-y-4">
            <Label className="text-base font-medium">Search Client</Label>

            {!isCreating ? (
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between h-12 text-base font-normal hover:bg-slate-50 border-slate-300"
                  >
                    <div className="flex items-center gap-2 text-slate-700">
                      <Building2 className="w-5 h-5 text-slate-400" />
                      {selectedClient
                        ? clients.find((client) => client.name === selectedClient)?.name || selectedClient
                        : "Select a client..."}
                    </div>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search clients..." className="h-11" />
                    <CommandList className="max-h-[220px]">
                      <CommandEmpty>No clients found.</CommandEmpty>
                      <CommandGroup>
                        {clients.map((client) => (
                          <CommandItem
                            key={client.id}
                            value={client.name}
                            onSelect={(currentValue) => {
                              setSelectedClient(currentValue === selectedClient ? null : currentValue)
                              setOpen(false)
                            }}
                            className="py-3 cursor-pointer"
                          >
                            <Building2 className="mr-2 h-4 w-4 text-slate-400" />
                            {client.name}
                            {selectedClient === client.name && (
                              <Check className="ml-auto h-4 w-4 text-brand-600" />
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                    <div className="p-2 border-t border-slate-100 bg-slate-50">
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start text-brand-600 hover:text-brand-700 hover:bg-brand-50"
                        onClick={() => {
                          setOpen(false)
                          setIsCreating(true)
                          setSelectedClient(null)
                        }}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Create New Client
                      </Button>
                    </div>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 space-y-4 animate-in fade-in slide-in-from-top-2">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-900">Create New Client</h3>
                  <Button variant="ghost" size="sm" onClick={() => setIsCreating(false)} className="h-8 text-slate-500 hover:text-slate-900">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Back to Search
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="v1-company">Company Name</Label>
                    <Input id="v1-company" placeholder="e.g. Acme Corp" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="v1-industry">Industry (Optional)</Label>
                    <Input id="v1-industry" placeholder="e.g. Aviation" />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setIsCreating(false)}>Cancel</Button>
                  <Button onClick={() => {
                    setSelectedClient("New Custom Company")
                    setIsCreating(false)
                  }}>Save & Select</Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}


// -------------------------------------------------------------------------------- //
// VARIATION 2: Segmented Tabs
// -------------------------------------------------------------------------------- //
function Variation2() {
  const [selectedClient, setSelectedClient] = useState<string | null>("Jet Star Aviation")

  return (
    <div className="space-y-4">
      <div className="border-b pb-2">
        <h2 className="text-xl font-semibold text-slate-900">Variation 2: Segmented Controls</h2>
        <p className="text-slate-500 text-sm">
          Reimagining the current approach using polished Tabs. It makes the two paths visually equal in weight
          and provides a very clear mental model without needing a dropdown popup for the primary layout.
        </p>
      </div>

      <Card className="shadow-sm border-slate-200">
        <CardContent className="p-6">
          <Tabs defaultValue="search" className="w-full">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
              <Label className="text-base font-medium">Client Selection</Label>
              <TabsList className="grid w-full sm:w-[340px] grid-cols-2">
                <TabsTrigger value="search" className="flex gap-2">
                  <Search className="w-4 h-4" />
                  Select Existing
                </TabsTrigger>
                <TabsTrigger value="create" className="flex gap-2">
                  <Plus className="w-4 h-4" />
                  Create New
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="search" className="mt-0 animate-in fade-in slide-in-from-left-2 data-[state=inactive]:hidden">
              <div className="relative">
                <Building className="absolute left-3.5 top-3.5 h-5 w-5 text-slate-400" />
                <select 
                  className="flex h-12 w-full rounded-md border border-slate-300 bg-transparent px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-11 appearance-none cursor-pointer hover:bg-slate-50 transition-colors"
                  value={selectedClient || ""}
                  onChange={(e) => setSelectedClient(e.target.value)}
                >
                  <option value="" disabled>Search or select client...</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-4 h-4 w-4 text-slate-500 pointer-events-none" />
              </div>
            </TabsContent>

            <TabsContent value="create" className="mt-0 animate-in fade-in slide-in-from-right-2 data-[state=inactive]:hidden">
              <div className="bg-white border text-card-foreground shadow-sm rounded-lg p-5">
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="v2-name">Company Name</Label>
                    <Input id="v2-name" placeholder="Enter company name..." className="h-10" />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="v2-contact">Primary Contact</Label>
                    <Input id="v2-contact" placeholder="Full name..." className="h-10" />
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}


// -------------------------------------------------------------------------------- //
// VARIATION 3: Progressive Disclosure (Link Reveal)
// -------------------------------------------------------------------------------- //
function Variation3() {
  const [isCreating, setIsCreating] = useState(false)

  return (
    <div className="space-y-4">
      <div className="border-b pb-2">
        <h2 className="text-xl font-semibold text-slate-900">Variation 3: Progressive Disclosure</h2>
        <p className="text-slate-500 text-sm">
          A very clean approach where the primary expected action (Search) is front and center. 
          The fallback action (Create) is a subtle text link that elegantly morphs the view.
        </p>
      </div>

      <Card className="shadow-sm border-slate-200 overflow-hidden">
        <CardContent className="p-0">
          <div className="p-6">
            <div className="flex items-end justify-between mb-2">
              <Label className="text-base font-medium">Search Client</Label>
              {!isCreating && (
                <button 
                  onClick={() => setIsCreating(true)}
                  className="text-sm text-brand-600 hover:text-brand-800 font-medium flex items-center transition-colors"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Can't find it? Create new
                </button>
              )}
            </div>

            {!isCreating ? (
              <div className="relative animate-in fade-in slide-in-from-top-2">
                <Search className="absolute left-3.5 top-3.5 h-5 w-5 text-slate-400" />
                <Input 
                  placeholder="Type to search clients..." 
                  className="pl-11 h-12 text-base bg-slate-50/50 border-slate-300 focus:bg-white" 
                />
              </div>
            ) : (
              <div className="rounded-md border border-brand-100 bg-brand-50/30 p-5 mt-2 animate-in fade-in slide-in-from-bottom-2 relative">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium text-slate-900 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center text-brand-600">
                      <Building2 className="w-4 h-4" />
                    </div>
                    New Client Details
                  </h3>
                  <button 
                    onClick={() => setIsCreating(false)}
                    className="text-sm text-slate-500 hover:text-slate-800 underline underline-offset-4"
                  >
                    Cancel
                  </button>
                </div>
                <div className="space-y-4 pl-10">
                  <div className="space-y-2">
                    <Input id="v3-name" placeholder="Company Name" className="h-10 bg-white" />
                  </div>
                  <div className="space-y-2">
                    <Input id="v3-email" placeholder="Billing Email" type="email" className="h-10 bg-white" />
                  </div>
                  <Button className="w-full">Create Client</Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
